import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController, Platform } from '@ionic/angular';
import { environment } from 'src/environments/environment';
import { NotificationClientService } from '../client/notification-client/notification-client.service';

export interface AgentTags {
  network_type?: string;
  region?: string;
  political_group?: string;
  agent_level?: string;
}

@Injectable({
  providedIn: 'root',
})
export class OneSignalService {
  private initialized = false;

  constructor(
    private router: Router,
    private platform: Platform,
    private notificationClient: NotificationClientService,
    private alertCtrl: AlertController
  ) {}

  initialize(): void {
    if (this.initialized) {return;}

    this.platform.ready().then(() => {
      if (!this.platform.is('capacitor')) {return;}

      const os = this.getInstance();
      if (!os) {return;}

      const appId = environment.onesignal?.appId;
      if (!appId) {
        console.warn('[OneSignal] appId not configured. Skipping initialization.');
        return;
      }

      try {
        console.log('[OneSignal] initializing with appId:', appId);
        os.initialize(appId);
        os.Notifications.requestPermission(true).then((granted: boolean) => {
          console.log('[OneSignal] permission granted:', granted);
        });
        os.User.pushSubscription.addEventListener('change', (change: any) => {
          console.log('[OneSignal] subscription change:', JSON.stringify(change));
        });
        os.Notifications.addEventListener('click', (event: any) => this.onNotificationClick(event));
        this.initialized = true;
        console.log('[OneSignal] initialized successfully');
      } catch (e) {
        console.error('[OneSignal] initialization error:', e);
      }
    });
  }

  /**
   * Identifica o device no OneSignal (External ID).
   *
   * ⚠️ CONTRATO: o valor TEM que ser o **ID DO AGENTE** (`agent.id` = co_agent),
   * porque é ele que o backend envia em `include_external_user_ids` ao disparar
   * as notificações (NotificationTargetingService). Passar o userId aqui faz
   * TODA push do sistema mirar um destinatário inexistente e nada chegar — foi
   * exatamente a regressão de 02/06 (bab8e34), invisível por semanas.
   * agentId e userId são 1:1 mas têm VALORES diferentes (UUIDs distintos).
   */
  login(externalId: string, tags?: AgentTags): void {
    if (!externalId) {
      console.warn('[OneSignal] login ignorado: externalId (agent.id) vazio.');
      return;
    }

    this.platform.ready().then(() => {
      if (!this.platform.is('capacitor')) {return;}

      const os = this.getInstance();
      if (!os) {return;}

      console.log('[OneSignal] login with agent.id (externalId):', externalId);
      os.login(externalId);

      if (tags) {
        const filtered: Record<string, string> = {};
        Object.entries(tags).forEach(([k, v]) => { if (v !== undefined) {filtered[k] = v;} });
        if (Object.keys(filtered).length > 0) {
          os.User.addTags(filtered);
        }
      }
    });
  }

  logout(): void {
    this.platform.ready().then(() => {
      if (!this.platform.is('capacitor')) {return;}
      this.getInstance()?.logout();
    });
  }

  private getInstance(): any {
    const w = window as any;
    const os = w?.plugins?.OneSignal ?? w?.OneSignal?.default ?? null;
    if (!os) {
      console.warn('[OneSignal] plugin instance not found on window.plugins.OneSignal or window.OneSignal.default');
    }
    return os;
  }

  private onNotificationClick(event: any): void {
    const data = event?.notification?.additionalData || {};
    const actionId: string = event?.result?.actionId || '';
    const missionId: string = data?.mission_id;
    const channelId: string = data?.channelId;
    const templateCode: string = data?.template_code;
    const onesignalMessageId: string = event?.notification?.notificationId;

    if (data?.type === 'ADMIN_EVENT') {
      this.showAdminNotificationAlert();
      return;
    }

    if (channelId) {
      this.router.navigate(['/chat', channelId]);
      return;
    }

    // Resultado da verificação da missão: sucesso leva ao Placar (conquistas),
    // falha leva ao detalhe da missão para revisão.
    if (templateCode === 'MISSION_VALIDATION_OK') {
      this.recordNotificationClick(onesignalMessageId, missionId, actionId);
      this.router.navigate(['/placar']);
      return;
    }
    if (templateCode === 'MISSION_VALIDATION_FAILED' && missionId) {
      this.recordNotificationClick(onesignalMessageId, missionId, actionId);
      this.router.navigate(['/mission-detail', missionId]);
      return;
    }

    if (!onesignalMessageId || !missionId) {return;}

    this.recordNotificationClick(onesignalMessageId, missionId, actionId);
  }

  private recordNotificationClick(onesignalMessageId: string, missionId: string, actionId: string): void {
    if (!onesignalMessageId || !missionId) {return;}

    this.notificationClient.recordEvent({
      onesignalMessageId,
      missionId,
      eventType: 'click',
      actionId: actionId || null,
      occurredAt: new Date().toISOString(),
    }).subscribe({
      error: (err) => console.error('Failed to record notification event', err),
    });
  }

  private async showAdminNotificationAlert(): Promise<void> {
    const alert = await this.alertCtrl.create({
      header: 'Notificação Administrativa',
      message: 'Esta é uma notificação administrativa. Acesse o Painel Web em seu desktop ou tablet para interagir com o conteúdo desta notificação.',
      buttons: ['Entendido'],
    });
    await alert.present();
  }
}
